import React, {Fragment, useEffect, useRef, useState} from "react";
import {abvNum, abvNumEx, getAgo} from "../tools/CommonJS";
import {classNameScrollbar, classNameScrollbarThin, dispDateTime, setClassNameScrollbar} from "../tools/CommonHtml";
import {Button} from "../tools/Inputs";
import {AnimatePickerRotation} from "../tools/UIIcons";
import {ToggleDiv} from "../tools/UIAnimateToggleDiv";
import {severitiesDefs} from "./CommonUIData";
import {tailCheck} from "../tools/CommonReact";
import {Badge, TextEllip} from "../tools/UIBlocks";
import {isElement} from "react-dom/test-utils";
import {genTooltip} from "./AppContext";


const classNameBaseScrollBar = "scrollbar scrollbar-thumb-badge/75 hover:scrollbar-thumb-badge scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-bg2";
setClassNameScrollbar(`${classNameBaseScrollBar} scrollbar-w-3`, `${classNameBaseScrollBar} scrollbar-thin`);

export const Title = ({className, classNameColor, ...props}) =>
    <TextEllip data-test="title" className={`${className} ${classNameColor || "text-title"}`} {...props}/>;

export const DashCard = ({className, ...props}) =>
    <div className={tailCheck(className, "rounded-lg", "bg-bg2", "shadow-card", "overflow-hidden")} {...props}/>;

export const Divider = ({className, classNameColor, vertical = false, ...props}) =>
    <div className={`shrink-0 ${vertical ? "w-[2px] self-stretch" : "h-[2px]"} ${classNameColor || "bg-divider"} ${className}`} {...props}/>

export const DashCardDivider = ({className}) =>
    <div className={`h-[2px] dark:bg-bg3 ${className}`}/>

export const GridDivider = ({className, style}) =>
    <div style={{gridColumn: "1/-1", ...style}} className={`bg-bg1 h-[2px] ${className}`}/>

export const DevicesSpreadInfo = ({ className, h, sum, subtitle }) =>
    <div data-test={`devices-spread-info-${h.toLowerCase().toKebabCase()}`} className={tailCheck(className, "gap-2", "flex flex-col", "text-title/80")}>
        <Title data-test="dsi-title" classNameColor="text-xs text-gray-300">{h}</Title>
        <Title data-test="dsi-value" classNameColor="text-lg">{typeof sum === "number"? sum.toLocaleString() : sum}</Title>
        <Title data-test="dsi-subtitle" classNameColor="text-xs grow flex self-baseline text-subtitle1">{subtitle}</Title>
    </div>;

export const BulletItem = ({Tag = Button, className, children,  ...props}) =>
    <Tag  data-test="bullet-item" className={`flex items-center gap-4 ${className}`} {...props}>
        <div className="aspect-square w-0.5 shrink-0 rounded-full bg-prm-main"/>
        {children}
    </Tag>;

export const ListItem = ({onClick, handleClr, leftTip, active, className, children, ...props}) =>
    <div {...{onClick, ...props}} className={tailCheck(className,
        "rounded-md flex items-center overflow-hidden", "gap-3 pr-3", "h-14", "mb-[0.6rem]",
        onClick && "cursor-pointer", active? "bg-bg4" : "bg-bg3")}
    >
        <div data-test="list-item-border" className={`w-2 h-full rounded-l-md shrink-0 min-w-0 ${active? "bg-prm-main" : severitiesDefs[handleClr]?.bg ?? handleClr ?? "bg-handle"}`}
             {...genTooltip(leftTip)} />
        {children}
    </div>;

export const ListItemTitleSub = ({title, subtitle, className, ...props}) =>
    <div className={`flex flex-col gap-0.5 min-w-0 ${className}`} {...props}>
        {typeof title === "string"? <Title className="text-sm">{title}</Title> : title}
        <Title className="text-xs font-sans" classNameColor="text-tx-cool-grey">{subtitle}</Title>
    </div>

export const TitleAbvNum = ({val, className, dataTest}) =>
    <Title data-test={dataTest} classNameColor={`w-full ${className || "text-title"}`}
           title={typeof val === "number" && val >= 1000? val : undefined}>
        {typeof val === "number" ? abvNumEx(val) : val || <>&nbsp;</>}
    </Title>;

export const ListItemValue = ({h, val, val2, className, classNameVal, classNameVal2, ...props}) =>
    <div data-test="list-item" className={tailCheck(className, "flex", "shrink-0", "min-w-0" ,"overflow-hidden", "flex-col")} {...props}>
        <div data-test="list-item-value"  className={`flex gap-1 text-center`}>
            {(val || val === 0) && <TitleAbvNum dataTest={`${h?.toLowerCase()}-list-item1`} val={val} className={classNameVal}/>}
            {(val || val === 0) && (val2 || val2 === 0) && "/"}
            {(val2 || val2 === 0) && <TitleAbvNum dataTest={`${h?.toLowerCase()}-list-item2`} val={val2} className={classNameVal2}/>}
        </div>
        {((val || val === 0) || (val2 || val2 === 0)) &&
            <Title data-test="list-item-title" classNameColor="text-tx-steel text-xs w-inherit text-center">{h}</Title>}
    </div>

export const TitleWithBadges = ({h, badges, className, dataTest}) =>
    <div className={`flex items-center gap-2 ${className}`}>
        <Title data-test={`${dataTest}-title`} className="grow" classNameColor=" ">{h}</Title>
        {badges?.map(({v, fTip, className}, i) =>
            <Badge data-test={`${dataTest}-badge-${i}`} key={i} className={tailCheck(className, "text-2xs text-bg1", "bg-icon", "px-1", "leading-3")} pill
                   title={fTip?.format(v.toLocaleString?.() ?? v) ?? (v > 999? v.toLocaleString() : undefined)}>
                {typeof v === "number" ? abvNum(v) : v}
            </Badge>)}
    </div>


export const PanelItem = ({h, val, className, classNameVal, ...props}) =>
    <div className={tailCheck(className, "flex min-w-0" ,"overflow-hidden", "flex-col")} {...props}>
        {(val ?? false) !== false && <>             {/* undefined or null or false */}
            <Title classNameColor={`text-tx-steel text-xs ${typeof val === "number" && "text-center"}`}>
                {h}
            </Title>
            <TitleAbvNum val={val} className={classNameVal}/>
        </>}
    </div>;

export const dispUserName = user => user?.userName ?? (user?.user ?? user)?.split('\\').last.before('@');
export const dispUserDesc = user => user?.userName? `${user?.userName} (${user?.user})` : user?.user ?? user;

export const LetterAvatar = ({user, className, noTip = false, ...props}) => {
    return <div data-test="letter-avatar"
        className={tailCheck(className, "bg-[#6c7987]", "flex items-center justify-center aspect-square rounded-full text-white")}
        {...(!noTip && genTooltip(dispUserDesc(user)))} {...props}
    >
        {dispUserName(user)?.split(/[ _.]/).filter((_,i,a) => !i || i === a.length-1).map(w => w[0]).join('').toUpperCase()}
    </div>
}

export const ListItemScanTime = ({h, tm, className}) =>
    <ListItemValue h={h ?? "Scanned"} val={getAgo(tm, {topOnly: 1})} title={dispDateTime(tm)} className={`text-xs w-14 items-end ${className}`}/>;

export const FilterEntry = ({title, children, className, info}) =>
    <div data-test={`${title?.toLowerCase().toKebabCase()}-filter`} className={`flex flex-col gap-2 ${className}`}>
        <div className="flex items-baseline gap-3"><Title data-test="filter-entry-title" className="text-sm" children={title}/>{info}</div>
        {children}
    </div>

export const FilterEntryHorizontal = ({title, children, className}) =>
    <div className={`flex items-center justify-end gap-4 ${className}`}>
        <Title className="text-xs text-tx-cool-grey">{title}</Title>
        {children}
    </div>

export const InfoCell = ({className, title, children}) => {
    return <div data-test={`${title.toLowerCase().toKebabCase()}-info-cell`} className={`text-xs ${className}`}>
        <Title data-test='info-cell-title' className="pb-0.5 text-xs font-bold" classNameColor="text-subtitle1" children={title}/>
        <div data-test='info-cell-value' className={`flex flex-col gap-1 `}>
            {children}
        </div>
    </div>
}

export const TableCard = ({filter, top, main, reset, filterTitle, topTitle, topDivider, classNameCard, classNameFilter, classNameMain}) =>
    <DashCard
        className={tailCheck(classNameCard, "grow min-w-0 min-h-[25rem] flex", "p-8", "gap-8", "h-full", classNameMain?.includes("min-w-") && "min-w-min")}>
        {filter && <div className={tailCheck(classNameFilter, "w-68", "shrink-0", "h-full", "flex flex-col", "items-stretch", "gap-7")}>
            <div className="pr-2 flex gap-6 items-baseline justify-between">
                <Title className="font-bold text-xl">{filterTitle ?? "Filter"}</Title>
                <Button data-test="reset-button" onClick={reset} className="text-sm text-prm-light">Reset</Button>
            </div>
            <Divider classNameColor="mr-2 bg-bg3"/>
            <div className={`pr-2 flex flex-col gap-5 items-stretch h-full overflow-auto ${classNameScrollbarThin}`}>
                {filter}
            </div>
        </div>}
        <div className={`grow flex min-w-0 flex-col gap-3 ${classNameMain} overflow-x-auto overflow-y-hidden`}>
            {(topTitle || top) &&
                <div className={`shrink-0 flex items-center gap-4`}>
                    {topTitle && <Title className="self-start grow text-xl">{topTitle}</Title>}
                    {top}
                </div>}
            {topDivider && <Divider classNameColor="bg-bg3"/>}
            {main}
        </div>
    </DashCard>;

/*
* you can let this component handle you state or handle it outside and pass on*/
export function AccordionDiv({isExpanded = null, onSetExpanded, summary, children, classNameDiv = "gap-2.5", classNameWrapper,
                                 arrow = false, handle = false, handleColor, scroll = false, className, initiallyOpen = false}) {
    const [privateIsShown, setPrivateIsShown] = useState( initiallyOpen);
    const isShown = isExpanded !== null ? isExpanded : privateIsShown

    return <div className={`flex items-stretch ${scroll && "overflow-y-auto"} ${classNameWrapper}`}>
        {handle && <div className={`w-[0.575rem] min-w-0 shrink-0 rounded-l-md ${handleColor || "bg-handle"}`}/>}
        <DashCard className={`flex flex-col grow shadow-sm ${handle && "rounded-none rounded-r-md"} ${className}`}>
            <div className="flex flex-wrap items-center cursor-pointer gap-2 w-full justify-between " onClick={e => isExpanded !== null ?  onSetExpanded?.(!isExpanded) : setPrivateIsShown(prevState => !prevState)}>
                {summary}
                {arrow && <div className={`pl-5 py-2 text-icon cursor-pointer`}>
                    <AnimatePickerRotation className={isShown ? "rotate-180" : "text-title font-bolder"}/>
                </div>}
            </div>
            <ToggleDiv className={`${scroll && "overflow-y-scroll"} ${classNameScrollbar}`} show={isShown}>
                <div className={`mt-4 flex flex-col ${classNameDiv}`}>
                    {children}
                </div>
            </ToggleDiv>
        </DashCard>
    </div>
}

export function ReadMoreParagraph({children, width, linesToShow, className}) {
    const [readMore, setReadMore] = useState(false)
    const pRef = useRef();
    const isOverflow = pRef?.current?.scrollHeight > pRef?.current?.clientHeight;

    return <div>
        <p ref={pRef} onClick={() => setReadMore(prevState => !prevState)}
           {...(readMore || {
               style: {
                   maxWidth: width,
                   WebkitLineClamp: linesToShow || 4,
                   display: '-webkit-box',
                   WebkitBoxOrient: 'vertical'
               }
           })}
           className={`text-ellipsis overflow-hidden ${isOverflow && "cursor-pointer"} ${className}`}>
            {children}
        </p>
        {isOverflow &&
            <span onClick={() => setReadMore(prevState => !prevState)} className="text-prm-light">Read {!readMore ? "more" : "less"}</span>}
    </div>;
}

export function ReadMoreWrapper({arr, elementRender, numToShow = 3}) {
    const [readMore, setReadMore] = useState(false)

    return <div>
        {arr.slice(0, readMore ? arr.length : numToShow ).map((a, i) => elementRender(a, i))}
        {arr.length > numToShow && <Title onClick={e => setReadMore((prevVal) => !prevVal)} className="cursor-pointer" classNameColor="text-prm-main">
            {readMore ? "read less" : `${arr.length - numToShow} more... >`}
        </Title>}
    </div>
}

export function UIFadeContent({children, fadeDep, className, ...rest}) {
    const vals = useRef({children}).current;
    const [isVisible, setVisibility] = useState(false);

    const fadeDependency = fadeDep !== undefined ? fadeDep : children;

    useEffect(() => {
        setVisibility(false);

        const tmChangeChildren = setTimeout(() => {
            vals.children = children;
            setVisibility(true);
        }, 200);

        return () => clearTimeout(tmChangeChildren);
    }, [fadeDependency]);

    return <div {...rest} className={`transition-opacity ${isVisible ? "opacity-100" : "opacity-50"} ${className}`}>
        {vals.children}
    </div>;
}


export const colorWheel = ["#cce1ff", "#93b4fd", "#757ccc", "#537dee", "#17adcc", "#0090d9", "#0079be", "#0e4380", "#696F7F"];

export const LegendBar = ({legends}) => <div className="flex items-center">
    {legends.map((def, i) => <div style={{background: def.c || colorWheel[i]}}
                                  className="py-1 px-2 text-prm-contrast first:rounded-l-lg font-semibold last:rounded-r-lg"
                                  key={i}>{def?.h} </div>)}
</div>;


export const DataListInfo = ({infoDefs, classNameH = "w-32", className, colNum = 3, gridTemplateColumns, fadeDep}) => {
    return <div style={{gridTemplateColumns: gridTemplateColumns || `min-content repeat(${colNum - 1}, minmax(0, 1fr))`}}
                className={`${className} grid grid-flow-row gap-2`}>
        {(Array.isArray(infoDefs) ? infoDefs : [infoDefs]).map((info, i) => {
            if (!info?.h && !info?.divider) return null;
            const {h, k, d, divider} = info;
            return <Fragment key={i}>{(k?.h || k?.[0]?.h || divider) && <Divider style={{gridColumn: `span ${colNum} / span ${colNum}`}}/>}
                {!divider && <Fragment key={h}>
                    <div data-test={`${h.toLowerCase().toKebabCase()}-data-list-info-title`} className={`${info.classNameH} ${classNameH} ${(k?.h || k?.[0]?.h) ? "text-md" : "text-xs"}`}>
                        {h}{(k?.h || k?.[0]?.h) && ":"}</div>
                    {(k?.h || k?.[0]?.h) ? <>
                            <div style={{gridColumn: `span ${colNum - 1} / span ${colNum - 1}`}}/>
                            <div  style={{overflowWrap: 'break-word', gridColumn: `span ${colNum} / span ${colNum}`}}>
                                <DataListInfo {...{classNameH, colNum}} infoDefs={k}/>
                            </div>
                        </> :
                        <UIFadeContent data-test={`${h.toLowerCase().toKebabCase()}-data-list-info-value`}
                            style={{overflowWrap: 'break-word', gridColumn: `span ${colNum - 1} / span ${colNum - 1}`}}
                            className={`text-xs text-desc`} fadeDep={fadeDep}>
                            {k?.[0]?.[0]?.h ? k?.map(v =>
                                    <div className="py-1 flex flex-col gap-1">
                                        <DataListInfo classNameH={classNameH} colNum={colNum - 1} infoDefs={v}/>
                                        <Divider/>
                                    </div>)
                                : Array.isArray(k) ? k.map(v => <>
                                        <div/>
                                        <ReadMoreParagraph className=" max-w-full">{v}</ReadMoreParagraph></>)
                                    : <ReadMoreParagraph className=" max-w-full">{isElement(k) ? k : k ? String(k) : "--"}</ReadMoreParagraph>}
                        </UIFadeContent>}
                </Fragment>}
            </Fragment>})}
            </div>;
}


export const SidePanelItemValue = ({title, value}) =>
    <div className="flex flex-col gap-1">
        <Title classNameColor="text-tx-steel">{title}</Title>
        <div className="ml-4 whitespace-pre-wrap">{value}</div>
    </div>;
